import { Box, Button, HStack, Text } from '@chakra-ui/react'
import { Header } from '@hivemq/ui-library'
import { useNavigate } from '@tanstack/react-router'
import {
  ActivityIcon,
  BarChart3Icon,
  BlocksIcon,
  Building,
  ClipboardListIcon,
  CloudIcon,
  DatabaseIcon,
  EyeIcon,
  LayoutGridIcon,
  type LucideIcon,
  MonitorIcon,
  RouteIcon,
  RouterIcon,
  ServerIcon,
  SettingsIcon,
  ShieldIcon,
  TableIcon,
  UsersIcon,
} from 'lucide-react'
import { useAuth } from '~/contexts/AuthContext'
import { createTrackedLinkHandler } from '~/lib/external-links'

type ExternalLink = { label: string; href: string; icon: LucideIcon }

const EXTERNAL_LINKS: ExternalLink[] = [
  {
    label: 'Central Control Center',
    href: import.meta.env.VITE_CENTRAL_CC_ADDRESS,
    icon: MonitorIcon,
  },
  { label: 'Cloud Console', href: 'https://app.hivemq.com', icon: CloudIcon },
  { label: 'Edge Control Panel', href: import.meta.env.VITE_EDGE_CC_ADDRESS, icon: RouterIcon },
  { label: 'Grafana', href: import.meta.env.VITE_GRAFANA_ADDRESS, icon: BarChart3Icon },
  { label: 'Prometheus', href: import.meta.env.VITE_PROMETHEUS_ADDRESS, icon: ActivityIcon },
  { label: 'Traefik', href: import.meta.env.VITE_TRAEFIK_ADDRESS, icon: RouteIcon },
  { label: 'PgAdmin', href: import.meta.env.VITE_PGADMIN_ADDRESS, icon: TableIcon },
  { label: 'DataHub Registry', href: 'https://dhrm.demo.hivemq.io/', icon: DatabaseIcon },
]

type AdminLink = { label: string; to: string; icon: LucideIcon }

const OPERATOR_LINKS: AdminLink[] = [
  { label: 'Kubernetes', to: '/admin/k8s', icon: ServerIcon },
  { label: 'Customers', to: '/admin/customers', icon: Building },
  { label: 'Playground Nodes', to: '/admin/playground-nodes', icon: BlocksIcon },
  { label: 'Flow Templates', to: '/admin/flow-templates', icon: LayoutGridIcon },
]

const ADMIN_ONLY_LINKS: AdminLink[] = [
  { label: 'Users', to: '/admin/users', icon: UsersIcon },
  { label: 'Settings', to: '/admin/settings', icon: SettingsIcon },
  { label: 'Audit Logs', to: '/admin/audit', icon: ClipboardListIcon },
  { label: 'User Activity', to: '/admin/activity', icon: EyeIcon },
]

// The ui-library Header.MenuButton hardcodes its icon to white (it assumed the
// old black header). On the new TopBar we force the icon to the theme's
// primary content colour so it stays legible in both light and dark mode.
function MenuButtonIcon({ ariaLabel, icon }: { ariaLabel: string; icon: LucideIcon }) {
  return (
    <Box
      color="content.primary"
      css={{ '& svg': { color: 'var(--chakra-colors-content-primary)' } }}
    >
      <Header.MenuButton aria-label={ariaLabel} icon={icon} />
    </Box>
  )
}

function ExternalLinkItem({ label, href, icon: Icon }: ExternalLink) {
  return (
    <Header.MenuContentItem ariaLabel={label}>
      <HStack w="100%" asChild>
        <a
          href={href}
          onClick={createTrackedLinkHandler(href, label)}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            width: '100%',
            color: 'white',
            textDecoration: 'none',
          }}
        >
          <Icon size={16} />
          <span>{label}</span>
        </a>
      </HStack>
    </Header.MenuContentItem>
  )
}

function AdminLinkItem({ label, to, icon: Icon }: AdminLink) {
  const navigate = useNavigate()
  return (
    <Header.MenuContentItem ariaLabel={label}>
      <HStack w="100%" asChild>
        <Button
          variant="plain"
          fontSize="md"
          color="white"
          onClick={() => navigate({ to })}
          justifyContent="flex-start"
        >
          <Icon size={16} />
          <span>{label}</span>
        </Button>
      </HStack>
    </Header.MenuContentItem>
  )
}

// External services launcher - its own top-bar-right menu.
export function LinksMenu() {
  return (
    <Header.Menu overlayId="links_menu">
      <MenuButtonIcon ariaLabel="External links" icon={LayoutGridIcon} />
      <Header.MenuContent>
        <Header.MenuContentDetails textAlign="center">
          <Text fontSize="1rem" fontWeight={600}>
            External Links
          </Text>
          <Text fontSize="sm" color="gray.500">
            Quick access to services
          </Text>
        </Header.MenuContentDetails>
        {EXTERNAL_LINKS.map((link) => (
          <ExternalLinkItem key={link.label} {...link} />
        ))}
      </Header.MenuContent>
    </Header.Menu>
  )
}

// Administration menu - its own top-bar-right menu, shown to operators and
// admins only. Operators see the operator links; admins additionally see the
// admin-only links. Nothing renders for regular users.
export function AdminMenu() {
  const { hasMinRole } = useAuth()
  const isOperator = hasMinRole('operator')
  const isAdmin = hasMinRole('admin')

  if (!isOperator) {
    return null
  }

  return (
    <Header.Menu overlayId="admin_menu">
      <MenuButtonIcon ariaLabel="Administration" icon={ShieldIcon} />
      <Header.MenuContent>
        <Header.MenuContentDetails textAlign="center">
          <Text fontSize="1rem" fontWeight={600}>
            Administration
          </Text>
          <Text fontSize="sm" color="gray.500">
            System management
          </Text>
        </Header.MenuContentDetails>
        {OPERATOR_LINKS.map((link) => (
          <AdminLinkItem key={link.label} {...link} />
        ))}
        {isAdmin && ADMIN_ONLY_LINKS.map((link) => <AdminLinkItem key={link.label} {...link} />)}
      </Header.MenuContent>
    </Header.Menu>
  )
}
